// Monitoring reference — the status model, freshness rules, design tokens and component
// inventory. Ported from the mockup rvDocs. Static reference content.

import { C, MONO, BM, CM, QM, RM, TM } from '../lib/tokens';

function LegendRow({ token, rule }: { token: readonly [string, string, string]; rule: string }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '3px 0' }}>
      <span style={{ fontFamily: MONO, color: token[1], width: 20, textAlign: 'center', flexShrink: 0 }} aria-hidden="true">
        {token[0]}
      </span>
      <span style={{ fontSize: 11.5, fontWeight: 600, width: 120, flexShrink: 0 }}>{token[2]}</span>
      <span style={{ fontSize: 11.5, color: C.dm }}>{rule}</span>
    </div>
  );
}

const TOKENS: Array<[string, string, string]> = [
  [C.bg, '--bg', 'App background'],
  [C.s1, '--surface', 'Cards & tables'],
  [C.s2, '--surface-2', 'Hover & wells'],
  [C.ln, '--line', 'Borders & dividers'],
  ['#E6ECF6', '--text', 'Primary text'],
  ['#8598B3', '--text-dim', 'Secondary text'],
  [C.bl, '--blue', 'Info · selection · long'],
  [C.gn, '--green', 'Healthy · profit'],
  [C.am, '--amber', 'Warning · delayed'],
  [C.rd, '--red', 'Critical · offline · loss'],
  [C.gy, '--gray', 'Stale · unknown'],
  [C.violet, '--violet', 'Short direction'],
];

export function Docs() {
  const card = { background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8, padding: '12px 16px' } as const;

  return (
    <section data-screen-label="Docs" style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 1000 }}>
      <h1 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Monitoring reference</h1>

      <div style={card}>
        <h2 style={{ margin: '0 0 4px', fontSize: 12.5, fontWeight: 600 }}>Status model — five independent dimensions, never merged</h2>
        <div style={{ fontSize: 11, color: C.dm, marginBottom: 8 }}>Every status is shown as icon + text + color; color alone is never the only signal.</div>

        <Group title="Connection health — terminal → collector (by server receive time)">
          <LegendRow token={CM.live!} rule="received ≤ 10 s" />
          <LegendRow token={CM.delayed!} rule="received 10–60 s" />
          <LegendRow token={CM.stale!} rule="received 1–3 min — values preserved, row dimmed" />
          <LegendRow token={CM.offline!} rule="no data > 3 min, or terminal/VPS down" />
        </Group>

        <Group title="Broker connection — terminal → trade server">
          <LegendRow token={BM.connected!} rule="trade server reachable" />
          <LegendRow token={BM.disconnected!} rule="terminal alive but broker unreachable — quotes freeze at last value" />
          <LegendRow token={BM.unknown!} rule="no telemetry to judge" />
        </Group>

        <Group title="Risk (client thresholds, editable in Settings)">
          <LegendRow token={RM.normal!} rule="inside all thresholds" />
          <LegendRow token={RM.warning!} rule="margin < warning, drawdown > warning, or daily loss > limit" />
          <LegendRow token={RM.critical!} rule="margin < critical, drawdown > critical, or equity ≤ 0" />
        </Group>

        <Group title="Trading state">
          <LegendRow token={TM.flat!} rule="no open positions" />
          <LegendRow token={TM.open!} rule="positions open — count shown in the cell" />
          <LegendRow token={TM.disabled!} rule="trading disabled / investor (read-only) login" />
          <LegendRow token={TM.inactive!} rule="market session closed — healthy heartbeat, old quotes" />
          <LegendRow token={TM.sync!} rule="terminal synchronizing after a restart" />
        </Group>

        <Group title="Data quality">
          <LegendRow token={QM.current!} rule="full snapshot received" />
          <LegendRow token={QM.partial!} rule="fields missing (permissions) — shown as “Unavailable”" />
          <LegendRow token={QM.unknown!} rule="stale or offline — nothing assumed current" />
        </Group>
      </div>

      <div style={card}>
        <h2 style={{ margin: '0 0 8px', fontSize: 12.5, fontWeight: 600 }}>Freshness &amp; update rules</h2>
        {[
          ['Heartbeat vs quote', 'Two independent ages. A healthy terminal can hold old quotes (market closed); a dead terminal can hold fresh-looking numbers — both are surfaced separately.'],
          ['Liveness source', 'Ages are computed from the server receive time, never from client or broker clocks. Between SSE updates the client keeps counting locally so a stalled account drifts live → delayed → stale → offline.'],
          ['Change flash', 'A changed cell highlights blue for ~0.9 s. Rows never strobe wholesale.'],
          ['Preserved data', 'Stale/offline rows dim to 72 % and keep their last values with exact timestamps on hover.'],
          ['Browser disconnect', 'Persistent banner; values freeze with an “as of” time; ages keep counting; automatic resync on reconnect discards out-of-order updates via revision numbers.'],
          ['Never shown', 'NaN, Infinity, misleading zeros, or color-only status.'],
        ].map(([k, v]) => (
          <div key={k} style={{ display: 'flex', gap: 12, padding: '5px 0', borderBottom: '1px solid #16203A' }}>
            <span style={{ fontSize: 11.5, fontWeight: 600, width: 150, flexShrink: 0 }}>{k}</span>
            <span style={{ fontSize: 11.5, color: C.dm, lineHeight: 1.5 }}>{v}</span>
          </div>
        ))}
      </div>

      <div style={card}>
        <h2 style={{ margin: '0 0 5px', fontSize: 12.5, fontWeight: 600 }}>Live-data protocol</h2>
        <div style={{ fontSize: 11.5, color: C.dm, lineHeight: 1.55 }}>
          The dashboard loads versioned REST snapshots (fleet summary, accounts, alerts), then subscribes to a tenant-scoped SSE stream. Each
          frame carries a monotonic <code>revision</code>; the client applies frames with revision greater than its snapshot, ignores stale
          replays, and re-fetches snapshots on an explicit <code>resync</code> event or a revision gap. Reconnects use exponential backoff with
          <code> Last-Event-ID</code> resume. There is no endpoint that can place, modify, or close a trade — acknowledge/resolve act only on
          server-side alert rows.
        </div>
      </div>

      <div style={card}>
        <h2 style={{ margin: '0 0 8px', fontSize: 12.5, fontWeight: 600 }}>Design tokens</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 20px' }}>
          {TOKENS.map((t) => (
            <div key={t[1]} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '3px 0' }}>
              <span style={{ width: 16, height: 16, borderRadius: 4, background: t[0], border: '1px solid rgba(255,255,255,.12)', flexShrink: 0 }} aria-hidden="true" />
              <span style={{ fontSize: 11, fontFamily: MONO, width: 86 }}>{t[1]}</span>
              <span style={{ fontSize: 11, fontFamily: MONO, color: C.dim2, width: 64 }}>{t[0]}</span>
              <span style={{ fontSize: 11, color: C.dm }}>{t[2]}</span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 10, borderTop: '1px solid ' + C.ln2, paddingTop: 8 }}>
          {[
            ['Interface type', 'IBM Plex Sans 400/500/600 · 10–16 px'],
            ['Numeric type', 'IBM Plex Mono, tabular numerals — logins, prices, P&L, timestamps'],
            ['Signs', 'Explicit + / − on all P&L; “Unavailable” for undefined — never NaN, ∞ or fake zeros'],
            ['Masking', 'Logins always masked to last 4 digits (server-side)'],
          ].map((r) => (
            <div key={r[0]} style={{ display: 'flex', gap: 12, padding: '4px 0' }}>
              <span style={{ fontSize: 11.5, fontWeight: 600, width: 150, flexShrink: 0 }}>{r[0]}</span>
              <span style={{ fontSize: 11.5, color: C.dm }}>{r[1]}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <>
      <div style={{ margin: '10px 0 4px', fontSize: 10.5, color: C.dm, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase' }}>{title}</div>
      {children}
    </>
  );
}
